import React, { useEffect, useState, useRef } from 'react';
import { Loader2, X, Check, AlertCircle, Sparkles, ArrowRight, FileText, Mic, MicOff, Beaker } from 'lucide-react';
import { tl } from '../../lib/testingLabApi';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';

/**
 * QuickReviewPanel — slide-over panel with a stack of plain-English test cards.
 *
 * Phases:
 *   "loading"   — generating cards
 *   "review"    — single-card view, user picks Pass / Fail / Pass-with-note
 *   "summary"   — review verdicts before submit
 *   "submitted" — done; offers Generate Change Order or View findings
 *
 * Props:
 *   open         — boolean
 *   onClose      — callback
 *   route        — current route (e.g. /data?tab=kanban)
 *   pageTitle    — friendly name (e.g. "Tasks → Kanban")
 *   onFindings   — optional callback called with the created findings array
 */
export default function QuickReviewPanel({ open, onClose, route, pageTitle, onFindings }) {
  const [phase, setPhase] = useState('loading');
  const [session, setSession] = useState(null);  // {session_id, cards}
  const [idx, setIdx] = useState(0);             // current card index
  const [verdicts, setVerdicts] = useState({});  // card_id → {verdict, note}
  const [activeNote, setActiveNote] = useState(null); // {card_id, verdict, draft}
  const [submitting, setSubmitting] = useState(false);
  const [findings, setFindings] = useState([]);
  const [recording, setRecording] = useState(false);
  const recognitionRef = useRef(null);

  // Bootstrap
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setPhase('loading');
      setIdx(0);
      setVerdicts({});
      setActiveNote(null);
      setFindings([]);
      try {
        const res = await tl.quickPlan({ route, page_title: pageTitle, use_llm: false });
        if (cancelled) return;
        setSession(res);
        setPhase('review');
      } catch (e) {
        toast.error('Could not start quick review: ' + (e.message || e));
        onClose?.();
      }
    })();
    return () => { cancelled = true; };
  }, [open, route, pageTitle]);  // eslint-disable-line

  if (!open) return null;

  const currentCard = session?.cards?.[idx];
  const totalCards = session?.cards?.length || 0;
  const counts = Object.values(verdicts).reduce(
    (a, v) => { a[v.verdict] = (a[v.verdict] || 0) + 1; return a; },
    { pass: 0, fail: 0, pass_with_note: 0 },
  );

  const recordVerdict = (verdict) => {
    if (!currentCard) return;
    if (verdict === 'fail' || verdict === 'pass_with_note') {
      setActiveNote({ card_id: currentCard.card_id, verdict, draft: '' });
      return;
    }
    const next = { ...verdicts, [currentCard.card_id]: { verdict, note: '' } };
    setVerdicts(next);
    advance(next);
  };

  const advance = (current = verdicts) => {
    if (idx + 1 < totalCards) {
      setIdx(idx + 1);
    } else {
      setPhase('summary');
    }
  };

  const confirmNote = () => {
    if (!activeNote) return;
    const next = { ...verdicts, [activeNote.card_id]: { verdict: activeNote.verdict, note: activeNote.draft } };
    setVerdicts(next);
    setActiveNote(null);
    advance(next);
  };

  const submitAll = async () => {
    setSubmitting(true);
    try {
      const verdictArr = session.cards.map((c) => ({
        card_id: c.card_id,
        verdict: verdicts[c.card_id]?.verdict || 'pass',
        note: verdicts[c.card_id]?.note || null,
      }));
      const res = await tl.quickSubmit({ session_id: session.session_id, verdicts: verdictArr });
      setFindings(res.findings);
      onFindings?.(res.findings);
      setPhase('submitted');
      toast.success(`Saved · ${res.summary.findings_created} findings created`);
    } catch (e) {
      toast.error('Submit failed: ' + (e.message || e));
    } finally {
      setSubmitting(false);
    }
  };

  // --- Voice dictation for note ---
  const startDictation = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      toast.error('Voice dictation not supported in this browser');
      return;
    }
    const rec = new SR();
    rec.continuous = true;
    rec.interimResults = true;
    rec.lang = 'en-US';
    rec.onresult = (e) => {
      let final = '';
      for (let i = e.resultIndex; i < e.results.length; i++) {
        if (e.results[i].isFinal) final += e.results[i][0].transcript;
      }
      if (final && activeNote) {
        setActiveNote((prev) => prev ? { ...prev, draft: (prev.draft + ' ' + final).trim() } : prev);
      }
    };
    rec.onerror = () => setRecording(false);
    rec.onend = () => setRecording(false);
    rec.start();
    recognitionRef.current = rec;
    setRecording(true);
  };

  const stopDictation = () => {
    recognitionRef.current?.stop();
    setRecording(false);
  };

  const close = () => {
    stopDictation();
    onClose?.();
  };

  // --- Render ---
  return (
    <div className="fixed inset-0 z-50 flex items-stretch justify-end" data-testid="quick-review-panel">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={close} />
      <div className="relative bg-card border-l border-border shadow-2xl w-full max-w-xl flex flex-col h-full">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-border">
          <div className="flex items-center gap-2">
            <Beaker className="w-4 h-4 text-violet-500" />
            <div>
              <div className="text-sm font-semibold">Quick review · {pageTitle || route}</div>
              <div className="text-xs text-muted-foreground">{route}</div>
            </div>
          </div>
          <button data-testid="qr-close" onClick={close} className="p-1.5 hover:bg-accent rounded-md">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-auto p-5 space-y-4">
          {phase === 'loading' && (
            <div className="flex flex-col items-center justify-center py-20 gap-3 text-muted-foreground">
              <Loader2 className="w-6 h-6 animate-spin text-violet-500" />
              <div className="text-sm">Building plain-English test cards for this page...</div>
            </div>
          )}

          {phase === 'review' && currentCard && !activeNote && (
            <>
              <div className="text-xs font-medium text-muted-foreground">
                Card {idx + 1} of {totalCards}
              </div>
              <div className="border border-border rounded-xl p-5 bg-card shadow-sm" data-testid="qr-card">
                <div className="flex items-start justify-between gap-3 mb-3">
                  <h2 className="text-lg font-semibold leading-tight">{currentCard.title}</h2>
                  <Badge variant="outline" className="capitalize text-xs">{currentCard.priority}</Badge>
                </div>
                <div className="space-y-3 text-sm">
                  <div>
                    <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">What to do</div>
                    <p>{currentCard.what_to_do}</p>
                  </div>
                  <div>
                    <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">What should happen</div>
                    <p className="text-foreground">{currentCard.expect}</p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-2 pt-2">
                <Button data-testid="qr-pass" className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  onClick={() => recordVerdict('pass')}>
                  <Check className="w-4 h-4 mr-1" /> Pass
                </Button>
                <Button data-testid="qr-pass-with-note" variant="outline"
                  className="border-amber-500/50 text-amber-700 dark:text-amber-300 hover:bg-amber-500/10"
                  onClick={() => recordVerdict('pass_with_note')}>
                  <Sparkles className="w-4 h-4 mr-1" /> Pass + note
                </Button>
                <Button data-testid="qr-fail" variant="destructive"
                  onClick={() => recordVerdict('fail')}>
                  <AlertCircle className="w-4 h-4 mr-1" /> Fail
                </Button>
              </div>

              <div className="text-xs text-muted-foreground text-center pt-2">
                {counts.pass} pass · {counts.fail} fail · {counts.pass_with_note} with notes
              </div>
            </>
          )}

          {/* Note capture */}
          {activeNote && (
            <div className="border border-border rounded-xl p-5 space-y-3" data-testid="qr-note-form">
              <div className="flex items-center gap-2 text-sm font-medium">
                {activeNote.verdict === 'fail'
                  ? <><AlertCircle className="w-4 h-4 text-red-500" /> Tell us what failed</>
                  : <><Sparkles className="w-4 h-4 text-amber-500" /> Add a note</>
                }
              </div>
              <p className="text-xs text-muted-foreground">
                Use plain English. Your exact words will be embedded verbatim in the Mini-PRD so the code agent treats them as authoritative intent.
              </p>
              <Textarea
                data-testid="qr-note-input"
                rows={5}
                placeholder="Describe what you saw, what you expected, and any device/context info..."
                value={activeNote.draft}
                onChange={(e) => setActiveNote({ ...activeNote, draft: e.target.value })}
                className="text-sm"
              />
              <div className="flex justify-between items-center">
                <Button variant="ghost" size="sm" onClick={recording ? stopDictation : startDictation}
                  data-testid="qr-mic">
                  {recording
                    ? <><MicOff className="w-4 h-4 mr-1.5 text-red-500" /> Stop dictation</>
                    : <><Mic className="w-4 h-4 mr-1.5" /> Dictate</>
                  }
                </Button>
                <div className="flex gap-2">
                  <Button variant="ghost" onClick={() => { setActiveNote(null); }}>Cancel</Button>
                  <Button data-testid="qr-note-confirm"
                    onClick={confirmNote} disabled={!activeNote.draft.trim()}>
                    Save & continue
                  </Button>
                </div>
              </div>
            </div>
          )}

          {phase === 'summary' && (
            <div data-testid="qr-summary">
              <h2 className="text-lg font-semibold mb-2">Review your verdicts</h2>
              <p className="text-sm text-muted-foreground mb-4">
                {counts.fail + counts.pass_with_note} {(counts.fail + counts.pass_with_note) === 1 ? 'finding' : 'findings'} will be created.
              </p>
              <div className="space-y-2 mb-6">
                {session.cards.map((c) => {
                  const v = verdicts[c.card_id]?.verdict || 'pass';
                  const cls = v === 'pass' ? 'border-emerald-500/40 bg-emerald-50 dark:bg-emerald-950/20'
                            : v === 'fail' ? 'border-red-500/40 bg-red-50 dark:bg-red-950/20'
                            : 'border-amber-500/40 bg-amber-50 dark:bg-amber-950/20';
                  return (
                    <div key={c.card_id} className={`border rounded-lg p-3 ${cls}`}>
                      <div className="flex items-start justify-between gap-2">
                        <div className="text-sm font-medium">{c.title}</div>
                        <Badge variant="outline" className="text-xs">{v}</Badge>
                      </div>
                      {verdicts[c.card_id]?.note && (
                        <p className="text-xs italic text-muted-foreground mt-1.5">"{verdicts[c.card_id].note}"</p>
                      )}
                    </div>
                  );
                })}
              </div>
              <div className="flex gap-2 justify-end">
                <Button variant="ghost" onClick={() => { setPhase('review'); setIdx(0); }}>
                  Re-do
                </Button>
                <Button data-testid="qr-submit" onClick={submitAll} disabled={submitting} className="bg-violet-600 hover:bg-violet-700 text-white">
                  {submitting ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <FileText className="w-4 h-4 mr-1.5" />}
                  Submit & create findings
                </Button>
              </div>
            </div>
          )}

          {phase === 'submitted' && (
            <div data-testid="qr-submitted" className="text-center py-8 space-y-4">
              <div className="w-16 h-16 mx-auto rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                <Check className="w-8 h-8 text-emerald-600" />
              </div>
              <h2 className="text-lg font-semibold">Findings saved</h2>
              <p className="text-sm text-muted-foreground">
                {findings.length} structured findings have been added with your verbatim notes. They are ready to be aggregated into a Change Order.
              </p>
              <div className="flex flex-col gap-2 max-w-xs mx-auto">
                <Button data-testid="qr-go-findings"
                  onClick={() => { onClose?.(); window.location.href = '/testing-lab?tab=findings'; }}
                  className="bg-violet-600 hover:bg-violet-700 text-white">
                  View findings <ArrowRight className="w-4 h-4 ml-1.5" />
                </Button>
                <Button variant="ghost" onClick={close}>Close</Button>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        {phase === 'review' && (
          <div className="border-t border-border px-5 py-2 text-xs text-muted-foreground flex justify-between">
            <span>Tap a verdict to advance.</span>
            <span>{idx + 1} / {totalCards}</span>
          </div>
        )}
      </div>
    </div>
  );
}
